import React, {useEffect, useState} from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Zoom,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper as MuiPaper,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTheme } from "@mui/material/styles";
import { getCarrerasAnteriores } from "../../../services/hipodromo";
import Loading from "../../../componetes/loading";
import {getColors} from "../../../componetes/color_racers";
import { useParams } from 'react-router-dom';

interface Jinete {
  id: number;
  nombre: string;
}

interface Preparador {
  id: number;
  nombre: string;
}

interface ProximaCarrera {
  inscripcion: number;
  peso_ejemplar: number;
  jinete: Jinete;
  preparador: Preparador;
}

interface CarreraHistorial {
  car_id: number;
  car_fecha: string;
  car_id_hipodromo: number;
  car_distancia: number;
  car_resultado: number;
  car_tiempo_final: string;
  jinete: Jinete;
  preparador: Preparador;
}

interface Ejemplar {
  ejemplar_id: number;
  ejemplar_nombre: string;
  proxima_carrera: ProximaCarrera;
  historial_carreras: CarreraHistorial[];
}

interface Props {
  id: number;
}
// Definimos el tipo para el objeto mockData
interface MockDataType {
  [key: string]: Ejemplar;
}


const CarrerasAnteriores: React.FC<Props> = ({id}) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState<MockDataType>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPanels, setExpandedPanels] = useState<{ [key: number]: boolean }>({});
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { hipodromoId } = useParams<{ hipodromoId: string }>();


  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Modificamos la función handleAccordionChange
  const handleAccordionChange = (ejemplarId: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels(prev => ({
      ...prev,
      [ejemplarId]: isExpanded
    }));
  };

  useEffect(() => {
    const fetchCarrerasAnteriores = async () => {
      try {
        setLoading(true);
        const currentId = hipodromoId ? Number(hipodromoId) : 1;
        const response = await getCarrerasAnteriores(id, currentId);
        setData(response);
        setError(null);
      } catch (err) {
        setError('Error al cargar las carreras anteriores');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCarrerasAnteriores();
  }, [id, hipodromoId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Loading />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const MobileView = () => (
    <Box p={2}>
      {/* Grid con las carreras anteriores */}
      <Grid container spacing={2}>
        {Object.values(data).map((ejemplar) => {
          const {color, textColor} = getColors(ejemplar.proxima_carrera.inscripcion);


          return (
            <Grid item xs={12} key={ejemplar.ejemplar_id}>
              <Card
                variant="outlined"
                sx={{
                  borderLeft: `10px solid ${color}`,
                  backgroundColor: "#fafafa"
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      backgroundColor: color,
                      color: textColor,
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      mb: 2
                    }}
                  >
                    <Typography variant="h5">
                      {ejemplar.ejemplar_nombre}
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" color="text.secondary">
                    Próxima Carrera
                  </Typography>
                  <Typography variant="body2">
                    Inscripción: {ejemplar.proxima_carrera.inscripcion} | Peso: {ejemplar.proxima_carrera.peso_ejemplar} kg
                  </Typography>
                  <Typography variant="body2">
                    Jinete: {ejemplar.proxima_carrera.jinete.nombre} | Preparador: {ejemplar.proxima_carrera.preparador.nombre}
                  </Typography>

                  <Box mt={2}>
                    <Accordion 
                      expanded={expandedPanels[ejemplar.ejemplar_id] ?? true}
                      onChange={handleAccordionChange(ejemplar.ejemplar_id)}
                      sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ padding: 0 }}
                      >
                        <Typography variant="subtitle1" color="text.secondary">
                          Historial de Carreras
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: 0 }}>
                        <List dense>
                          {ejemplar.historial_carreras.map((carrera: CarreraHistorial) => (
                            <React.Fragment key={carrera.car_id}>
                              <ListItem>
                                <ListItemText
                                  primary={`Fecha: ${new Date(carrera.car_fecha).toLocaleDateString()}, Distancia: ${carrera.car_distancia}`}
                                  secondary={`Resultado: ${carrera.car_resultado}, Tiempo: ${carrera.car_tiempo_final}, Jinete: ${carrera.jinete.nombre}, Preparador: ${carrera.preparador.nombre}`}
                                />
                              </ListItem>
                              <Divider component="li" />
                            </React.Fragment>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Botón de scroll */}
      <Zoom in={showScrollTop}>
        <Fab 
          color="primary" 
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: { xs: 70, sm: 80 },
            right: { xs: 76, sm: 84 }, // Ajustado para dar espacio al botón de simulación
            zIndex: 1000
          }}
          aria-label="scroll back to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>


    </Box>
  );

  const DesktopView = () => (
    <Box p={2}>

      <Grid container spacing={3}>
        {Object.values(data).map((ejemplar) => {
          const {color, textColor} = getColors(ejemplar.proxima_carrera.inscripcion);
          return (
            <Grid item xs={12} key={ejemplar.ejemplar_id}>
              <Card
                variant="outlined"
                sx={{
                  borderLeft: `10px solid ${color}`,
                  backgroundColor: "white",
                  mb: 3 // Espacio entre cada reporte de ejemplar
                }}
              >
                <CardContent>
                  {/* Sección de Información del Ejemplar */}
                  <Box
                    sx={{
                      backgroundColor: color,
                      color: textColor,
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h5">
                      {ejemplar.ejemplar_nombre}
                    </Typography>
                    <Typography variant="h6">
                      Inscripción: {ejemplar.proxima_carrera.inscripcion}
                    </Typography>
                  </Box>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body1"><strong>Peso (Próx.):</strong> {ejemplar.proxima_carrera.peso_ejemplar} kg</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body1"><strong>Jinete (Próx.):</strong> {ejemplar.proxima_carrera.jinete.nombre}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body1"><strong>Preparador (Próx.):</strong> {ejemplar.proxima_carrera.preparador.nombre}</Typography>
                    </Grid>
                  </Grid>

                  {/* Sección de Historial de Carreras */}
                  <Typography variant="h6" gutterBottom>Historial de Carreras</Typography>
                  <TableContainer component={MuiPaper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Distancia</TableCell>
                          <TableCell>Resultado</TableCell>
                          <TableCell>Tiempo</TableCell>
                          <TableCell>Jinete</TableCell>
                          <TableCell>Preparador</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ejemplar.historial_carreras.map((carrera: CarreraHistorial) => (
                          <TableRow key={carrera.car_id}>
                            <TableCell>{new Date(carrera.car_fecha).toLocaleDateString()}</TableCell>
                            <TableCell>{carrera.car_distancia}m</TableCell>
                            <TableCell>{carrera.car_resultado}</TableCell>
                            <TableCell>{carrera.car_tiempo_final}</TableCell>
                            <TableCell>{carrera.jinete.nombre}</TableCell>
                            <TableCell>{carrera.preparador.nombre}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Botón de scroll */}
      <Zoom in={showScrollTop}>
        <Fab 
          color="primary" 
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
          aria-label="scroll back to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Box>
  );

  return (
    <Box p={2}>
      {isMobile ? <MobileView /> : <DesktopView />}
    </Box>
  );
};

export default CarrerasAnteriores;